import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Button, Select } from '@artosera/ui';
import { useEvenements, type EvenementPresente } from '../../api/evenements.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { NouvelEvenementDialog } from './NouvelEvenementDialog.js';
import styles from './Evenements.module.css';
import { Verrouillable } from '../module/LectureSeule.js';

export function CompteursEvenement({ evenement }: { evenement: EvenementPresente }) {
  const { t } = useTranslation('regie');
  const texte = `${evenement.placesConfirmees} / ${evenement.jauge ?? t('evenements.sansLimite')}`;
  return (
    <div>
      <p>
        {t('evenements.remplissage')} : {texte}
      </p>
      {evenement.jauge !== null ? (
        <progress
          aria-label={t('evenements.remplissage')}
          aria-valuetext={texte}
          value={Math.min(evenement.placesConfirmees, evenement.jauge)}
          max={evenement.jauge}
        >
          {texte}
        </progress>
      ) : null}
      <p>{t('evenements.attente', { count: evenement.placesEnAttente })}</p>
    </div>
  );
}
export function EvenementsPage() {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [periode, setPeriode] = useState<'a_venir' | 'passes'>('a_venir');
  const [nouveau, setNouveau] = useState(false);
  const lire = galerieALaction(galerie, 'events:read');
  const liste = useEvenements(galerieSlug, { periode }, lire);
  if (!lire) return <p role="alert">{t('evenements.accesRefuse')}</p>;
  return (
    <section className={styles.page}>
      <header className={styles.entete}>
        <h1>{t('evenements.titre')}</h1>
        <Verrouillable action="events:write" ouvert={galerieALaction(galerie, 'events:write')}>
          <Button onClick={() => setNouveau(true)}>{t('evenements.nouveau')}</Button>
        </Verrouillable>
      </header>
      <Select
        aria-label={t('evenements.periode')}
        value={periode}
        onValueChange={(v) => setPeriode(v as typeof periode)}
        options={['a_venir', 'passes'].map((v) => ({
          value: v,
          label: t(`evenements.periodes.${v}`),
        }))}
      />
      {liste.isPending ? <p role="status">{t('commun:chargement')}</p> : null}
      {liste.isError ? <p role="alert">{t('commun:erreurReseau')}</p> : null}
      {liste.data?.evenements.length === 0 ? <p>{t('evenements.aucun')}</p> : null}
      <div className={styles.liste}>
        {liste.data?.evenements.map((e) => (
          <article key={e.id} className={styles.carte}>
            <h2>
              <Link to={`/${galerieSlug}/evenements/${e.id}`}>
                {e.numero} · {e.titre[i18n.language === 'en' ? 'en' : 'fr'] || e.titre.fr}
              </Link>
            </h2>
            <p>
              {e.dates.debut
                ? new Date(e.dates.debut).toLocaleString(i18n.language)
                : t('evenements.phases.sans_date')}
            </p>
            <p>
              {t(`evenements.types.${e.type}`)} · {t(`evenements.phases.${e.phase}`)}
            </p>
            <CompteursEvenement evenement={e} />
          </article>
        ))}
      </div>
      {nouveau ? <NouvelEvenementDialog open onOpenChange={setNouveau} /> : null}
    </section>
  );
}
